import { useEffect, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { landingPageAnalyticsService, LandingPageStats, VisitsOverTime, RecentActivity } from '../../services/admin/landing-page-analytics.service';
import { Card, CardContent } from '../../components/ui/card';
import { DataTable } from '../../components/ui/data-table';
import { Users, Eye, Activity, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
} from 'recharts';
import toast from 'react-hot-toast';

const activityColumns: ColumnDef<RecentActivity>[] = [
  {
    id: 'eventType',
    header: 'Event',
    cell: ({ row }) => (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#d7785b]/10 text-[#d7785b]">
        {row.original.eventType}
      </span>
    ),
    meta: { width: 'w-40' },
  },
  {
    id: 'page',
    header: 'Page',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">{row.original.page}</span>
    ),
  },
  {
    id: 'device',
    header: 'Device',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground capitalize">
        {row.original.device || 'Unknown'}
      </span>
    ),
    meta: { width: 'w-28' },
  },
  {
    id: 'os',
    header: 'OS',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground capitalize">
        {row.original.os || 'Unknown'}
      </span>
    ),
    meta: { width: 'w-28' },
  },
  {
    id: 'browser',
    header: 'Browser',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground capitalize">
        {row.original.browser || 'Unknown'}
      </span>
    ),
    meta: { width: 'w-28' },
  },
  {
    id: 'createdAt',
    header: 'Time',
    cell: ({ row }) => (
      <span className="text-sm text-muted-foreground">
        {new Date(row.original.createdAt).toLocaleString()}
      </span>
    ),
    meta: { width: 'w-44' },
  },
  {
    id: 'details',
    header: 'Details',
    cell: ({ row }) =>
      row.original.eventData && Object.keys(row.original.eventData).length > 0 ? (
        <span className="text-xs bg-muted px-2 py-1 rounded">
          {Object.keys(row.original.eventData).length} fields
        </span>
      ) : (
        <span className="text-sm text-muted-foreground">-</span>
      ),
    meta: { width: 'w-24' },
  },
];

export default function LandingPageAnalytics() {
  const [stats, setStats] = useState<LandingPageStats | null>(null);
  const [visitsOverTime, setVisitsOverTime] = useState<VisitsOverTime[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('30');
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');
  const [selectedActivity, setSelectedActivity] = useState<RecentActivity | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchRecentActivity();
  }, [timeframe]);

  useEffect(() => {
    fetchVisitsOverTime();
  }, [timeframe, groupBy]);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(timeframe));

      const data = await landingPageAnalyticsService.getStats(startDate, new Date());
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch landing page stats:', error);
      toast.error('Failed to load landing page analytics');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVisitsOverTime = async () => {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(timeframe));

      const data = await landingPageAnalyticsService.getVisitsOverTime(
        startDate,
        new Date(),
        groupBy
      );
      setVisitsOverTime(data);
    } catch (error) {
      console.error('Failed to fetch visits over time:', error);
    }
  };

  const fetchRecentActivity = async () => {
    try {
      const data = await landingPageAnalyticsService.getRecentActivity(50);
      setRecentActivity(data);
    } catch (error) {
      console.error('Failed to fetch recent activity:', error);
    }
  };

  const handleActivityClick = (activity: RecentActivity) => {
    setSelectedActivity(activity);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedActivity(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 w-64 bg-muted rounded mb-2" />
          <div className="h-5 w-96 bg-muted rounded" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="animate-pulse border-border bg-transparent">
              <CardContent className="p-6">
                <div className="h-12 bg-muted rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-foreground">Landing Page Analytics</h1>
          <p className="text-muted-foreground">Track visitor activity and engagement metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timeframe} onValueChange={setTimeframe}>
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Key Metrics */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-border bg-transparent">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Total Visits</p>
                  <p className="text-3xl font-bold text-foreground">
                    {stats.totalVisits.toLocaleString()}
                  </p>
                </div>
                <Eye className="h-12 w-12 text-[#d7785b]/20" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border bg-transparent">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Unique Visitors</p>
                  <p className="text-3xl font-bold text-foreground">
                    {stats.uniqueVisitors.toLocaleString()}
                  </p>
                </div>
                <Users className="h-12 w-12 text-[#d7785b]/20" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Visits Over Time Chart */}
      <Card className="border-border bg-transparent">
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">Visits Over Time</h2>
              <Select value={groupBy} onValueChange={(v) => setGroupBy(v as any)}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Daily</SelectItem>
                  <SelectItem value="week">Weekly</SelectItem>
                  <SelectItem value="month">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={visitsOverTime}>
                <defs>
                  <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#d7785b" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#d7785b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey="visits"
                  stroke="#d7785b"
                  fillOpacity={1}
                  fill="url(#colorVisits)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Device & Referrer Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Device Breakdown */}
        <Card className="border-border bg-transparent">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Device Breakdown</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={Object.entries(stats?.deviceBreakdown || {}).map(([device, count]) => ({ device, count }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
                <XAxis dataKey="device" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#d7785b" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Pages */}
        <Card className="border-border bg-transparent">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Top Pages</h2>
            <div className="space-y-2">
              {stats?.topPages.map((page) => (
                <div key={page.page} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm text-muted-foreground">{page.page}</span>
                  <span className="font-semibold text-foreground">{page.visits.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Events & Countries */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Events */}
        <Card className="border-border bg-transparent">
          <CardContent className="p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Top Events</h2>
            <div className="space-y-2">
              {stats?.topEvents.slice(0, 8).map((event) => (
                <div key={event.eventType} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <span className="text-sm text-muted-foreground">{event.eventType}</span>
                  <span className="font-semibold text-foreground">{event.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity Table */}
      <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-4">
          <Activity className="h-5 w-5 text-[#d7785b]" />
          <h2 className="text-lg font-semibold text-foreground">Recent Activity</h2>
        </div>
        <DataTable
          columns={activityColumns}
          data={recentActivity}
          getRowId={(row) => row.id}
          onRowClick={handleActivityClick}
          emptyText="No recent activity found"
        />
      </div>

          {/* Activity Details Modal */}
          {isModalOpen && selectedActivity && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-border">
                  <h3 className="text-lg font-semibold text-foreground">Activity Details</h3>
                  <button
                    onClick={closeModal}
                    className="text-muted-foreground hover:text-muted-foreground transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                
                <div className="p-6 space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Event Type</label>
                      <p className="mt-1">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#d7785b]/10 text-[#d7785b]">
                          {selectedActivity.eventType}
                        </span>
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Page</label>
                      <p className="mt-1 text-sm text-foreground">{selectedActivity.page}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Device</label>
                      <p className="mt-1 text-sm text-foreground capitalize">{selectedActivity.device || 'Unknown'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Operating System</label>
                      <p className="mt-1 text-sm text-foreground capitalize">{selectedActivity.os || 'Unknown'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Browser</label>
                      <p className="mt-1 text-sm text-foreground capitalize">{selectedActivity.browser || 'Unknown'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Session ID</label>
                      <p className="mt-1 text-sm text-foreground font-mono">{selectedActivity.sessionId}</p>
                    </div>
                    <div className="col-span-2">
                      <label className="text-sm font-medium text-muted-foreground">Timestamp</label>
                      <p className="mt-1 text-sm text-foreground">
                        {new Date(selectedActivity.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Event Data */}
                  {selectedActivity.eventData && Object.keys(selectedActivity.eventData).length > 0 && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Event Data</label>
                      <div className="mt-2 bg-muted rounded-lg p-4">
                        <pre className="text-xs text-muted-foreground whitespace-pre-wrap overflow-x-auto">
                          {JSON.stringify(selectedActivity.eventData, null, 2)}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Raw Data */}
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Raw Event Data</label>
                    <div className="mt-2 bg-muted rounded-lg p-4">
                      <pre className="text-xs text-muted-foreground whitespace-pre-wrap overflow-x-auto">
                        {JSON.stringify(selectedActivity, null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end p-6 border-t border-border">
                  <button
                    onClick={closeModal}
                    className="px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-muted transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }
